import type {
  CheckProps,
  IntrinsicSystemProps,
  MotionPropsFor,
  UseCheckboxProps,
} from '@hover/blueprint';
import {
  Body,
  Center,
  Check,
  VisuallyHidden,
  calc,
  cssVariables,
  forwardRef,
  motion,
  presenceVariants,
  splitLayoutProps,
  system,
  transition,
  useBoolean,
  useCheckbox,
  useTheme,
} from '@hover/blueprint';
import type { Sizes } from '@hover/blueprint/foundation';
import type { MotionValue } from 'framer-motion';
import {
  AnimatePresence,
  animate,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
} from 'framer-motion';
import type { ReactElement, ReactNode, RefObject } from 'react';
import React, { useId, useImperativeHandle } from 'react';

import { useBlurFade } from 'src/modules/homeownerApp/helpers';

const checkedDiameter = 150 satisfies keyof Sizes;
const uncheckedDiameter = 50 satisfies keyof Sizes;

const $haloGap = cssVariables.space(200);
const $hoverGrow = cssVariables.space(100);

const $tooltipOffset = cssVariables.space(200);
const $tooltipGap = cssVariables.space(400);
const tooltipPosition = calc.multiply($tooltipOffset, -1);

const haloDiameter = calc.add(cssVariables.size(checkedDiameter), $haloGap);
const hoverDiameter = calc.add(haloDiameter, $hoverGrow);

interface CheckMaskProps extends CheckProps {
  id: string;
  children: ReactElement;
  scale?: number;
}

const MotionCenter = motion(Center);

/**
 * Wrapper for the `Check` component that applies the `Check` path itself as a
 * mask to a circle to "punch out" the checkmark while still animating it
 */
const CheckMask = forwardRef<CheckMaskProps, 'svg'>(
  ({ id, children, scale, ...props }, ref) => (
    <motion.svg
      {...props}
      animate={{ scale }}
      overflow="visible"
      ref={ref}
      viewBox="-8 -8 48 48"
    >
      <rect
        fill="currentColor"
        height={48}
        mask={`url(#${id})`}
        width={48}
        x={-8}
        y={-8}
      />
      <mask id={id}>
        <system.g
          fill="none"
          stroke="#000"
          sx={{ path: { strokeWidth: '5px' } }}
        >
          <circle cx="16" cy="16" fill="#fff" r="50%" stroke="none" />
          {children}
        </system.g>
      </mask>
    </motion.svg>
  ),
);

export interface HandleFunctionsRef {
  /**
   * Trigger a single pulse animation on the handle
   */
  pulse(): void;
}

export type TooltipDirection = 'start' | 'end';

type DivProps = IntrinsicSystemProps<'div'>;
type TooltipAnchor = Pick<
  MotionPropsFor<DivProps>,
  'top' | 'right' | 'bottom' | 'left'
>;

export interface SegmentHandleProps
  extends UseCheckboxProps,
    Omit<DivProps, keyof UseCheckboxProps> {
  tooltipDirection?: { x: TooltipDirection; y: TooltipDirection };
  handleFunctionsRef?: RefObject<HandleFunctionsRef>;
  isVisible?: boolean;
  isTooltipOpen?: boolean;
  pulseScale?: number;
  label: ReactNode;
  tooltip?: ReactNode;
}

export const SegmentHandle = forwardRef<SegmentHandleProps, 'div'>(
  (
    {
      tooltipDirection: { x: xDirection, y: yDirection } = {
        x: 'end',
        y: 'end',
      },
      handleFunctionsRef,
      label,
      isVisible = true,
      isTooltipOpen,
      pulseScale = 1.25,
      tooltip,
      zIndex,
      ...props
    },
    ref,
  ) => {
    const maskId = useId();
    const { layoutProps, props: checkboxProps } = splitLayoutProps(props);

    const {
      getLabelProps,
      getRootProps,
      getInputProps,
      state: { isChecked },
    } = useCheckbox(checkboxProps);

    const diameter = isChecked ? checkedDiameter : uncheckedDiameter;

    const { colors } = useTheme();

    const haloBlur = useBlurFade(isVisible);

    const scale = useMotionValue(1);
    const [isPulsing, { on: onPulseStart, off: onPulseStop }] =
      useBoolean(false);

    useImperativeHandle(handleFunctionsRef, () => ({
      pulse: () => {
        animate(scale, [1, pulseScale, 1], {
          duration: 0.75,
          ease: 'easeInOut',
        });
      },
    }));

    useMotionValueEvent(scale, 'animationStart', onPulseStart);
    useMotionValueEvent(scale, 'animationComplete', onPulseStop);

    const innerScale = useTransform(
      useSpring(scale, { damping: 15, stiffness: 325 }) as MotionValue<number>,
      [1, pulseScale],
      [1, pulseScale * 1.25],
    );

    const tooltipPadding = calc.add($tooltipOffset, haloDiameter, $tooltipGap);
    const tooltipAnchor: TooltipAnchor = {
      [xDirection === 'start' ? 'right' : 'left']: tooltipPosition,
      [yDirection === 'start' ? 'bottom' : 'top']: tooltipPosition,
    };

    return (
      <system.div
        position="relative"
        userSelect="none"
        {...getRootProps()}
        {...layoutProps}
        zIndex={zIndex}
      >
        <Center
          _disabled={{ cursor: 'not-allowed' }}
          as="label"
          borderRadius="round"
          cursor="pointer"
          data-group
          height={hoverDiameter}
          position="absolute"
          sx={{
            outline: '2px solid transparent',
            outlineOffset: '2px',
            '&:has(input:focus-visible)': { outlineColor: 'lightAlpha.700' },
            _dark: {
              '&:has(input:focus-visible)': { outlineColor: 'darkAlpha.600' },
            },
            ...transition.easing250('outline'),
          }}
          transform="translate(-50%, -50%)"
          width={hoverDiameter}
          {...getLabelProps()}
        >
          <AnimatePresence>
            {isTooltipOpen && isVisible && (
              <motion.div
                backdropFilter="blur(25px)"
                bg={colors.alpha(0.6, 'neutral.900')}
                borderRadius="large"
                overflow="hidden"
                position="absolute"
                transition={{
                  opacity: { duration: 0.4 },
                  backdropFilter: {
                    delay: 0.1,
                    duration: 0.75,
                  },
                }}
                userSelect="none"
                zIndex={1}
                {...tooltipAnchor}
                {...presenceVariants({
                  animate: { opacity: 1, backdropFilter: 'blur(25px)' },
                  initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                  exit: { opacity: 0 },
                })}
              >
                <system.div
                  boxSizing="border-box"
                  height="fit-content"
                  pb={yDirection === 'end' ? '400' : tooltipPadding}
                  pt={yDirection === 'start' ? '400' : tooltipPadding}
                  px="400"
                  width="600"
                >
                  <Body color="neutral.0" isBold m={0} size="small">
                    {tooltip}
                  </Body>
                </system.div>
              </motion.div>
            )}
          </AnimatePresence>
          <MotionCenter
            _dark={{ bg: 'darkAlpha.400' }}
            _groupDisabled={{
              width: `${haloDiameter} !important`,
              height: `${haloDiameter} !important`,
            }}
            _groupFocus={{ bg: 'darkAlpha.500' }}
            _groupHover={{
              width: hoverDiameter,
              height: hoverDiameter,
            }}
            backdropFilter="blur(20px)"
            bg="lightAlpha.400"
            borderRadius="round"
            height={haloDiameter}
            position="absolute"
            style={{ scale }}
            sx={{
              ...haloBlur,
              ...(isPulsing ? {} : transition.easing250('height', 'width')),
            }}
            width={haloDiameter}
            zIndex={2}
          />
          <MotionCenter
            _groupActive={{ transform: 'scale(0.9)' }}
            _groupDisabled={{ opacity: 0.25, transform: 'scale(1) !important' }}
            height={diameter}
            position="absolute"
            style={{ scale: innerScale }}
            sx={
              isPulsing
                ? undefined
                : transition.easing250('width', 'height', 'transform')
            }
            width={diameter}
            zIndex={3}
          >
            <Check
              as={CheckMask}
              bg="darkAlpha.400"
              borderRadius="round"
              color="neutral.0"
              height="100%"
              id={maskId}
              isChecked={isChecked}
              width="100%"
            />
          </MotionCenter>
          <VisuallyHidden>
            <input {...getInputProps()} ref={ref} />
            {label}
          </VisuallyHidden>
        </Center>
      </system.div>
    );
  },
);
